import { Component, inject, input } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatCardModule } from '@angular/material/card';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { AiConnectionsForm, ConnectionMetadata } from '../../core/forms';
import { buildUrlWithQueryParams } from '../../core/utils/navigation.helper';
import { FeatureFlagService } from '@galaxy/partner';
import { PARTNER_ID_TOKEN } from '../../core/tokens';
import { catchError, iif, map, of, switchMap } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'ai-connections-form',
  imports: [
    AiAssistantI18nModule,
    MatCardModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    FormsModule,
    MatCheckboxModule,
    GalaxyLoadingSpinnerModule,
  ],
  templateUrl: './ai-connections-form.component.html',
  styleUrl: './ai-connections-form.component.scss',
})
export class AiConnectionsFormComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly ffService = inject(FeatureFlagService);

  connectionForms = input.required<AiConnectionsForm>();

  protected showFBInsta = toSignal(
    inject(PARTNER_ID_TOKEN).pipe(
      switchMap((partnerId) =>
        iif(
          () => !!partnerId,
          this.ffService.batchGetStatus(partnerId, '', ['fb_insta_ai_responder']).pipe(
            map((status) => status['fb_insta_ai_responder'] ?? false),
            catchError(() => of(false)),
          ),
          of(false),
        ),
      ),
    ),
  );

  protected triggerAction(metadata: ConnectionMetadata): void {
    if (metadata.cta?.action) {
      this.buildConnectionActionCallback(metadata)?.();
    }
  }

  protected buildConnectionActionCallback(metadata: ConnectionMetadata): (() => void) | undefined {
    if (!metadata?.cta?.action?.showButton) {
      return undefined;
    }

    const action = metadata.cta.action;

    if (action?.url) {
      if (action.newTab && !action.relativeRoute) {
        const finalUrl = buildUrlWithQueryParams(action.url, action.queryParams);
        return () => window.open(finalUrl, '_blank');
      }

      const navigationExtras: NavigationExtras = action.relativeRoute ? { relativeTo: this.route } : {};
      if (action.queryParams) {
        navigationExtras.queryParams = action.queryParams;
      }

      return () => this.router.navigate([action.url], navigationExtras);
    }

    if (action?.pathCommands) {
      return () => this.router.navigate(action.pathCommands);
    }

    if (action?.callback) {
      return action.callback;
    }
    return undefined;
  }
}
